import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { delay } from '~/common/utils/async.util';
import { getPurchasesApi } from '~/features/purchases/api/get-purchases.api';

import type { Nullable } from '~/common/types/nullable.type';
import type { PaginationMeta } from '~/common/types/pagination.type';
import type { PurchaseItem, PurchaseListParams } from '~/features/purchases/types/purchases.type';

export interface PurchasesState {
  isLoading: boolean;
  items: PurchaseItem[];
  params: Nullable<PurchaseListParams>;
  meta: PaginationMeta | null;
}

const initialState: PurchasesState = {
  isLoading: false,
  items: [],
  params: null,
  meta: null,
};

export const fetchPurchases = createAsyncThunk(
  'purchases/fetchPurchases',
  async (params: PurchaseListParams | undefined, { rejectWithValue }) => {
    try {
      const [{ data }] = await Promise.all([getPurchasesApi(params), delay(200)]);
      return { data, params };
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const PurchasesSlice = createSlice({
  name: 'purchases',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchPurchases.pending, (state, action) => {
        state.isLoading = true;
        state.params = action.meta.arg || null;
      })
      .addCase(fetchPurchases.fulfilled, (state, action) => {
        const page = action.payload.params?.page ?? 1;
        if (page > 1) {
          state.items.push(...action.payload.data.data);
        } else {
          state.items = action.payload.data.data;
        }
        state.meta = action.payload.data.meta;
        state.isLoading = false;
      })
      .addCase(fetchPurchases.rejected, state => {
        state.isLoading = false;
        state.items = [];
        state.meta = null;
      });
  },
});

export default PurchasesSlice;
